import React, { createContext, ReactNode, use, useState } from 'react'

interface StepWizardContextProps {
  currentStep: number
  totalSteps: number
  minStep: number
  setTotalSteps: (step: number) => void
  setCurrentStep: (step: number) => void
  next: () => void
  prev: () => void
}

const StepWizardContext = createContext<StepWizardContextProps | undefined>(
  undefined
)

interface StepWizardProviderProps {
  children: ReactNode
  currentStep: number
  setCurrentStep: (step: number) => void
  totalSteps?: number
  // Floor for `prev()` / navigation. Used to lock users out of earlier steps
  // (e.g. an authenticated user in the register wizard must never be able to
  // navigate back to the credentials step). Defaults to 0 so existing callers
  // are unaffected.
  minStep?: number
}

export const StepWizardProvider = ({
  children,
  currentStep,
  totalSteps: totalStepsInitialValue = 0,
  setCurrentStep,
  minStep = 0,
}: StepWizardProviderProps) => {
  const [totalSteps, setTotalSteps] = useState(totalStepsInitialValue)
  const next = () => {
    setCurrentStep(Math.min(currentStep + 1, totalSteps))
  }
  const prev = () => setCurrentStep(Math.max(currentStep - 1, minStep))

  return (
    <StepWizardContext.Provider
      value={{
        currentStep,
        totalSteps,
        minStep,
        setTotalSteps,
        setCurrentStep,
        next,
        prev,
      }}
    >
      {children}
    </StepWizardContext.Provider>
  )
}

export const useStepWizard = (): StepWizardContextProps => {
  const context = use(StepWizardContext)
  if (!context) {
    throw new Error('useStepWizard must be used within a StepWizardProvider')
  }
  return context
}
